<template>
    <div class="container">
        <schoolAndDate :schoolName="schoolName" :dateTime="dateTime" />
        <div class="wrap" ref="viewport">
            <transition name="page-turn-fade" mode="out-in">
                <div class="meal-list-wrap" :key="currentPageKey">
                    <div class="meal-list" v-for="(section, sectionIndex) in currentPage.sections" :key="section.sectionKey + '-' + sectionIndex">
                        <div class="meal-date">{{ section.sectionHeader.endDate || '' }} {{ section.sectionHeader.startDate || '' }}</div>

                        <div class="meal-block">
                            <div class="meal-item meal-head">
                                <div class="meal-title meal-col-label"><span>餐次</span></div>
                                <div class="meal-title meal-col-recipe"><span>营养食谱</span></div>
                                <div class="meal-title meal-col-ingredient"><span>食材组成</span></div>
                            </div>
                            <div class="meal-item" v-for="row in section.rows" :key="row.rowKey">
                                <div class="meal-content meal-col-label"><span>{{ row.mealLabel }}</span></div>
                                <div class="meal-content meal-col-recipe"><span>{{ row.recipeText }}</span></div>
                                <div class="meal-content meal-col-ingredient"><span>{{ row.ingredientText }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <div class="measure-layer" aria-hidden="true">
                <div class="meal-list-wrap measure-wrap">
                    <div class="meal-list" v-for="(section, sectionIndex) in displaySections" :key="buildSectionKey(section, sectionIndex) + '-measure'">
                        <div class="meal-date" :ref="'measureHeader-' + sectionIndex">{{ section.endDate || '' }} {{ section.startDate || '' }}</div>
                        <div class="meal-block">
                            <div class="meal-item meal-head" :ref="'measureHead-' + sectionIndex">
                                <div class="meal-title meal-col-label"><span>餐次</span></div>
                                <div class="meal-title meal-col-recipe"><span>营养食谱</span></div>
                                <div class="meal-title meal-col-ingredient"><span>食材组成</span></div>
                            </div>
                            <div class="meal-item" v-for="(row, rowIndex) in section.rows" :key="buildRowKey(section, sectionIndex, row, rowIndex)" :ref="'measureRow-' + sectionIndex + '-' + rowIndex">
                                <div class="meal-content meal-col-label"><span>{{ row.mealLabel }}</span></div>
                                <div class="meal-content meal-col-recipe"><span>{{ row.recipeText }}</span></div>
                                <div class="meal-content meal-col-ingredient"><span>{{ row.ingredientText }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PageTurnControl class="page-turn-control-position" :current="currentPageIndex + 1" :total="pages.length" theme="meal" @prev="goPrevPage" @next="goNextPage" />
        </div>
    </div>
</template>

<script>
import { buildNutritionalRecipeSections } from '../assets/js/nutritionalRecipeRows';
import { paginateMeasuredSections } from '../assets/js/contentPaging';
import { canAutoTurnPage, getNextPageIndex, getPrevPageIndex } from '../assets/js/pageTurn';
import PageTurnControl from './PageTurnControl.vue';
import schoolAndDate from './schoolAndDate.vue';

export default {
    props: {
        deptId: {
            type: String,
            default: ''
        },
        outletsId: {
            type: String,
            default: ''
        },
        dateTime: {
            type: String,
            default: '',
        },
        schoolName: {
            type: String,
            default: '',
        },
        tjcData: {
            type: Array,
            default: function () {
                return [];
            }
        }
    },
    components: { PageTurnControl, schoolAndDate },
    data() {
        return {
            infos: [],
            pages: [],
            currentPageIndex: 0,
            pageTimer: null,
        };
    },
    computed: {
        displaySections() {
            return buildNutritionalRecipeSections({
                menuList: this.infos,
                tjcData: this.tjcData,
            });
        },
        currentPage() {
            return this.pages[this.currentPageIndex] || { sections: [] };
        },
        currentPageKey() {
            return 'page-' + this.currentPageIndex;
        },
        autoplayDuration() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            var autoplay = query && query.autoplay ? parseInt(query.autoplay, 10) : 0;

            if (isNaN(autoplay) || autoplay <= 0) {
                return 0;
            }

            return autoplay;
        },
    },
    watch: {
        infos: {
            deep: true,
            handler: function () {
                this.rebuildPages();
            },
        },
        tjcData: {
            deep: true,
            handler: function () {
                this.rebuildPages();
            },
        },
        '$route.query.autoplay': function () {
            this.startPageTimer();
        },
    },
    mounted() {
        this.getMenuPriceAnnouncementList();
        window.addEventListener('resize', this.rebuildPages);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.rebuildPages);
        this.stopPageTimer();
    },
    methods: {
        getMenuPriceAnnouncementList() {
            var param = {
                outletsId: this.outletsId,
                recipeType: 0
            };
            this.$get(this.$api.getMenuPriceAnnouncementList, param).then((res) => {
                this.infos = Array.isArray(res && res.data) ? res.data : [];
            });
        },
        stopPageTimer() {
            if (this.pageTimer) {
                clearInterval(this.pageTimer);
                this.pageTimer = null;
            }
        },
        startPageTimer() {
            var vm = this;
            this.stopPageTimer();
            if (!canAutoTurnPage(this.pages.length, this.autoplayDuration)) {
                return;
            }
            this.pageTimer = setInterval(function () {
                vm.currentPageIndex = getNextPageIndex(vm.currentPageIndex, vm.pages.length);
            }, this.autoplayDuration);
        },
        restartPageTimer() {
            this.stopPageTimer();
            this.startPageTimer();
        },
        goPrevPage() {
            this.currentPageIndex = getPrevPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        goNextPage() {
            this.currentPageIndex = getNextPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        getRefElement(refName) {
            var refValue = this.$refs[refName];
            return Array.isArray(refValue) ? refValue[0] : refValue;
        },
        buildSectionKey(section, sectionIndex) {
            return [section && section.startDate || '', section && section.endDate || '', sectionIndex].join('-');
        },
        buildRowKey(section, sectionIndex, row, rowIndex) {
            return [this.buildSectionKey(section, sectionIndex), row && row.mealKey || '', rowIndex].join('-');
        },
        copyRowWithKey(rowKey, row) {
            return {
                rowKey: rowKey,
                mealKey: row && row.mealKey ? row.mealKey : '',
                mealName: row && row.mealName ? row.mealName : '',
                priceText: row && row.priceText ? row.priceText : '',
                mealLabel: row && row.mealLabel ? row.mealLabel : '',
                recipeText: row && row.recipeText ? row.recipeText : '',
                ingredientText: row && row.ingredientText ? row.ingredientText : '',
                visible: !!(row && row.visible),
            };
        },
        getMeasuredSection(section, sectionIndex) {
            var headerEl = this.getRefElement('measureHeader-' + sectionIndex);
            var headEl = this.getRefElement('measureHead-' + sectionIndex);
            var rows = section.rows.map(function (row, rowIndex) {
                var rowKey = this.buildRowKey(section, sectionIndex, row, rowIndex);
                var rowEl = this.getRefElement('measureRow-' + sectionIndex + '-' + rowIndex);
                return {
                    rowKey: rowKey,
                    measuredHeight: rowEl ? rowEl.offsetHeight : 0,
                    payload: this.copyRowWithKey(rowKey, row),
                };
            }, this);

            return {
                sectionKey: this.buildSectionKey(section, sectionIndex),
                sectionHeader: {
                    startDate: section && section.startDate ? section.startDate : '',
                    endDate: section && section.endDate ? section.endDate : '',
                },
                headerHeight: (headerEl ? headerEl.offsetHeight : 0) + (headEl ? headEl.offsetHeight : 0),
                rows: rows,
            };
        },
        getMeasureWrapInset() {
            var measureWrap = this.$el ? this.$el.querySelector('.measure-wrap') : null;
            var styles;

            if (!measureWrap) {
                return 0;
            }

            styles = window.getComputedStyle(measureWrap);
            return (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
        },
        getSectionGap() {
            var firstSection = this.$el ? this.$el.querySelector('.measure-wrap .meal-list') : null;
            var styles;

            if (!firstSection) {
                return 0;
            }

            styles = window.getComputedStyle(firstSection);
            return parseFloat(styles.marginBottom) || 0;
        },
        rebuildPages() {
            this.$nextTick(function () {
                var viewport = this.$refs.viewport;
                var pageInset = this.getMeasureWrapInset();
                var sectionGap = this.getSectionGap();
                var measuredSections = this.displaySections.map(function (section, index) {
                    return this.getMeasuredSection(section, index);
                }, this);

                this.pages = paginateMeasuredSections({
                    maxPageHeight: viewport ? viewport.clientHeight : 0,
                    pageInset: pageInset,
                    sectionGap: sectionGap,
                    sections: measuredSections,
                }).map(function (page) {
                    return {
                        pageIndex: page.pageIndex,
                        sections: page.sections.map(function (section) {
                            return {
                                sectionKey: section.sectionKey,
                                sectionHeader: section.sectionHeader,
                                continued: section.continued,
                                rows: section.rows.map(function (row) {
                                    return row.payload;
                                }),
                            };
                        }),
                    };
                });

                this.currentPageIndex = 0;
                this.startPageTimer();
            });
        },
    }
};
</script>

<style lang="scss" scoped>
$fontColor: #0A7B23;
$borderColor: #59C06F;
$wrapHeight: 830;

.container {
    position: relative;
}

.wrap {
    position: relative;
    height: vh($wrapHeight);
    width: 100%;
    overflow: hidden;
    border-radius: 3px;
    font-weight: bold;
    background: #b2f5c2;
}

.meal-list-wrap {
    padding: vh(12) vh(18) vh(58);
}

.page-turn-control-position {
    position: absolute;
    left: 50%;
    bottom: vh(8);
    z-index: 8;
    transform: translateX(-50%);
}

.meal-list {
    margin-bottom: vh(20);
}

.meal-date {
    color: #000;
    text-align: left;
    font-size: vh(22);
    margin-bottom: vh(10);
}

.meal-block {
    overflow: hidden;
    color: $fontColor;
    border-radius: vh(10);
    border: 1px solid $borderColor;
}

.meal-item {
    display: flex;
    font-size: vh(16);
    white-space: normal;
    text-align: left;
    border-bottom: 1px solid $borderColor;

    > .meal-title,
    > .meal-content {
        border-left: 1px solid $borderColor;
        padding: vh(10) vh(12);
        display: flex;
        align-items: center;

        &:first-of-type {
            border-left: none;
        }
    }

    &:last-of-type {
        border-bottom: none;
    }
}

.meal-title {
    font-size: vh(14);
    text-align: center;
    justify-content: center;
    background: #8EDE9F;
}

.meal-content {
    word-wrap: break-word;
    line-height: 1.4;

    span {
        line-height: 1.2;
    }
}

.meal-col-label {
    flex: 1.2;
    justify-content: center;
    text-align: center;
}

.meal-col-recipe {
    flex: 1.4;
}

.meal-col-ingredient {
    flex: 1.4;
}

.meal-head {
    .meal-title {
        min-height: vh(44);
    }
}

.measure-layer {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    visibility: hidden;
    pointer-events: none;
    z-index: -1;
}

.measure-wrap {
    padding: vh(12) vh(18);
}

.page-turn-fade-enter-active,
.page-turn-fade-leave-active {
    transition: opacity 0.45s ease, transform 0.45s ease;
}

.page-turn-fade-enter,
.page-turn-fade-leave-to {
    opacity: 0;
    transform: translateX(vh(24));
}
</style>
